import React, { useState } from 'react';
import VideoUpload from './VideoUpload';
import PromptSelector from './PromptSelector';
import ResultDisplay from './ResultDisplay';

const AuthenticatedContent = (props) => {
    // Check if auth is disabled
    const authDisabled = process.env.REACT_APP_DISABLE_AUTH === 'true';

    // State for managing active tab in processed videos
    const [activeTab, setActiveTab] = useState(null);

    // Auto-select first completed video tab
    React.useEffect(() => {
        if (props.uploadQueue) {
            const completedVideos = props.uploadQueue.filter(item => item.status === 'completed');
            if (completedVideos.length > 0 && !activeTab) {
                setActiveTab(completedVideos[0].id);
            }
        }
    }, [props.uploadQueue, activeTab]);
    
    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Video Query Tool</h1>
                {!authDisabled && (
                    <div className="d-flex align-items-center">
                        <small className="text-muted">Authentication enabled</small>
                    </div>
                )}
            </div>

            {/* Processing Mode Toggle */}
            <div className="mb-4">
                <div className="btn-group w-100" role="group" aria-label="Processing mode">
                    <button
                        type="button"
                        className={`btn ${props.processingMode === 'single' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => props.onProcessingModeChange('single')}
                    >
                        <strong>Single Video Mode</strong>
                        <br />
                        <small>Process videos individually</small>
                    </button>
                    <button
                        type="button"
                        className={`btn ${props.processingMode === 'batch' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => props.onProcessingModeChange('batch')}
                    >
                        <strong>Batch Mode</strong>
                        <br />
                        <small>Combine multiple videos (max 10)</small>
                    </button>
                </div>
            </div>

            {/* Always show video upload area */}
            <VideoUpload onVideoSelect={props.onVideoSelect} processingMode={props.processingMode} />

            {/* SINGLE VIDEO MODE - Queue Display */}
            {props.processingMode === 'single' && props.uploadQueue && props.uploadQueue.length > 0 && (
                <div className="mb-4">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                Single Video Queue ({props.uploadQueue.length} videos)
                                {props.uploadQueue.filter(item => item.status === 'processing').length > 0 && (
                                    <span className="badge bg-primary ms-2">
                                        {props.uploadQueue.filter(item => item.status === 'processing').length} processing
                                    </span>
                                )}
                            </h5>
                            <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={props.onClearQueue}
                            >
                                Clear All
                            </button>
                        </div>
                        <div className="card-body p-0">
                            <div className="list-group list-group-flush">
                                {props.uploadQueue.map((item, index) => (
                                    <div key={item.id} className="list-group-item">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center mb-2">
                                                    {/* Status Badge */}
                                                    {item.status === 'uploading' && (
                                                        <span className="badge bg-info me-2">
                                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                                            Uploading
                                                        </span>
                                                    )}
                                                    {item.status === 'uploaded' && (
                                                        <span className="badge bg-success me-2">✓ Ready</span>
                                                    )}
                                                    {item.status === 'processing' && (
                                                        <span className="badge bg-primary me-2">
                                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                                            Processing
                                                        </span>
                                                    )}
                                                    {item.status === 'cancelled' && (
                                                        <span className="badge bg-warning text-dark me-2">⚠️ Cancelled</span>
                                                    )}

                                                    <strong>{item.fileName}</strong>
                                                </div>

                                                {/* Upload Progress Bar */}
                                                {item.status === 'uploading' && (
                                                    <div className="progress mb-2" style={{height: '20px'}}>
                                                        <div
                                                            className="progress-bar progress-bar-striped progress-bar-animated bg-info"
                                                            role="progressbar"
                                                            style={{width: `${item.uploadProgress || 0}%`}}
                                                        >
                                                            {item.uploadProgress || 0}%
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Processing indicator */}
                                                {item.status === 'processing' && (
                                                    <div className="progress mb-2" style={{height: '20px'}}>
                                                        <div
                                                            className="progress-bar progress-bar-striped progress-bar-animated"
                                                            role="progressbar"
                                                            style={{width: '100%'}}
                                                        >
                                                            Processing...
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Error Message */}
                                                {item.error && (
                                                    <div className="alert alert-danger alert-sm mb-0 mt-2">
                                                        <small>{item.error}</small>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="d-flex flex-column gap-2 ms-2">
                                                {/* Process Button - Only for uploaded videos */}
                                                {item.status === 'uploaded' && (
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => props.onStartSingleVideoProcessing(item.id)}
                                                        title="Process this video"
                                                    >
                                                        ▶️ Process Video
                                                    </button>
                                                )}

                                                {/* Stop Button - Only for processing videos */}
                                                {item.status === 'processing' && (
                                                    <button
                                                        className="btn btn-sm btn-warning"
                                                        onClick={() => props.onCancelProcessing(item.id)}
                                                        title="Stop processing"
                                                    >
                                                        ⏸️ Stop
                                                    </button>
                                                )}

                                                {/* Retry Button - Only for failed videos */}
                                                {item.status === 'failed' && (
                                                    <button
                                                        className="btn btn-sm btn-info"
                                                        onClick={() => props.onRetryProcessing(item.id)}
                                                        title="Retry"
                                                    >
                                                        🔄 Retry
                                                    </button>
                                                )}

                                                {/* Remove Button - Always visible except when processing */}
                                                {item.status !== 'processing' && (
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => props.onRemoveFromQueue(item.id)}
                                                        title="Remove from queue"
                                                    >
                                                        🗑️ Remove
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* BATCH MODE - Queue Display with Reorder Controls */}
            {props.processingMode === 'batch' && props.batchQueue && props.batchQueue.length > 0 && (
                <div className="mb-4">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                Batch Queue ({props.batchQueue.length}/10 videos)
                                {props.batchQueue.filter(item => item.status === 'processing').length > 0 && (
                                    <span className="badge bg-primary ms-2">
                                        <span className="spinner-border spinner-border-sm me-1"></span>
                                        Processing Batch
                                    </span>
                                )}
                            </h5>
                            <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={props.onClearQueue}
                                disabled={props.batchQueue.some(item => item.status === 'processing')}
                            >
                                Clear All
                            </button>
                        </div>
                        <div className="card-body p-0">
                            <div className="list-group list-group-flush">
                                {props.batchQueue.map((item, index) => (
                                    <div key={item.id} className="list-group-item">
                                        <div className="d-flex justify-content-between align-items-start">
                                            {/* Order Number */}
                                            <div className="d-flex align-items-center me-3">
                                                <span className="badge bg-secondary" style={{fontSize: '1rem', minWidth: '35px'}}>
                                                    {index + 1}
                                                </span>
                                            </div>

                                            {/* Video Info */}
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center mb-2">
                                                    {/* Status Badge */}
                                                    {item.status === 'uploading' && (
                                                        <span className="badge bg-info me-2">
                                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                                            Uploading
                                                        </span>
                                                    )}
                                                    {item.status === 'uploaded' && (
                                                        <span className="badge bg-success me-2">✓ Ready</span>
                                                    )}
                                                    {item.status === 'processing' && (
                                                        <span className="badge bg-primary me-2">
                                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                                            Processing
                                                        </span>
                                                    )}

                                                    <strong>{item.fileName}</strong>
                                                </div>

                                                {/* Upload Progress */}
                                                {item.status === 'uploading' && (
                                                    <div className="progress mb-2" style={{height: '20px'}}>
                                                        <div
                                                            className="progress-bar progress-bar-striped progress-bar-animated bg-info"
                                                            style={{width: `${item.uploadProgress || 0}%`}}
                                                        >
                                                            {item.uploadProgress || 0}%
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Error Message */}
                                                {item.error && (
                                                    <div className="alert alert-danger alert-sm mb-0 mt-2">
                                                        <small>{item.error}</small>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Reorder and Action Buttons */}
                                            <div className="d-flex gap-2 ms-2">
                                                {/* Reorder buttons - only when not processing */}
                                                {!props.batchQueue.some(v => v.status === 'processing') && (
                                                    <>
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => props.onMoveBatchItem(index, 'up')}
                                                            disabled={index === 0}
                                                            title="Move up"
                                                        >
                                                            ↑
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => props.onMoveBatchItem(index, 'down')}
                                                            disabled={index === props.batchQueue.length - 1}
                                                            title="Move down"
                                                        >
                                                            ↓
                                                        </button>
                                                    </>
                                                )}

                                                {/* Remove button */}
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => props.onRemoveFromBatch(item.id)}
                                                    disabled={item.status === 'processing'}
                                                    title="Remove from batch"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Prompt Selector - Always Visible */}
            <PromptSelector
                mode={props.mode}
                onModeChange={props.onModeChange}
                prompt={props.prompt}
                onPromptChange={props.onPromptChange}
                disabled={props.isProcessingQueue}
            />

            {props.error && (
                <div className="alert alert-danger mb-3">
                    {props.error}
                </div>
            )}

            {/* BATCH MODE - Process Batch Button */}
            {props.processingMode === 'batch' && props.batchQueue && props.batchQueue.length > 0 && (
                <div className="d-grid gap-2 mb-4">
                    <button
                        className="btn btn-success btn-lg"
                        onClick={props.onProcessBatch}
                        disabled={
                            props.batchQueue.some(v => v.status !== 'uploaded') ||
                            props.batchQueue.some(v => v.status === 'processing')
                        }
                    >
                        {props.batchQueue.some(v => v.status === 'processing') ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Processing Batch...
                            </>
                        ) : (
                            <>
                                ▶️ Process Batch ({props.batchQueue.length} videos)
                            </>
                        )}
                    </button>
                    {props.batchQueue.some(v => v.status !== 'uploaded' && v.status !== 'processing' && v.status !== 'completed') && (
                        <small className="text-muted text-center">
                            Please wait for all videos to finish uploading
                        </small>
                    )}
                </div>
            )}

            {/* BATCH MODE - Batch Result Display */}
            {props.processingMode === 'batch' && props.batchResult && (
                <div className="mt-4 mb-4">
                    <div className="card">
                        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                ✅ Batch Processing Complete
                            </h5>
                            <span className="badge bg-light text-dark">
                                {props.batchResult.videosProcessed} videos | {props.batchResult.processingTime}
                            </span>
                        </div>
                        <div className="card-body">
                            <ResultDisplay
                                result={props.batchResult.content}
                                isLoading={false}
                                uploadProgress={100}
                                fileName={`Batch of ${props.batchResult.videosProcessed} videos`}
                            />
                        </div>
                    </div>
                </div>
            )}
            
            {/* SINGLE MODE - Processed List - Tabbed Interface for Completed and Failed Videos */}
            {props.processingMode === 'single' && props.uploadQueue && props.uploadQueue.some(item => ['completed', 'failed'].includes(item.status)) && (
                <div className="mt-4">
                    <div className="card">
                        <div className="card-header bg-success text-white">
                            <h5 className="mb-0">
                                ✅ Processed Videos ({props.uploadQueue.filter(item => ['completed', 'failed'].includes(item.status)).length})
                            </h5>
                        </div>
                        <div className="card-body p-0">
                            {/* Horizontal Tabs Navigation */}
                            <ul className="nav nav-tabs" role="tablist" style={{flexWrap: 'nowrap', overflowX: 'auto', borderBottom: '1px solid #dee2e6'}}>
                                {props.uploadQueue.filter(item => ['completed', 'failed'].includes(item.status)).map((item, index) => (
                                    <li className="nav-item" role="presentation" key={item.id} style={{flexShrink: 0}}>
                                        <button
                                            className={`nav-link ${activeTab === item.id ? 'active' : ''} d-flex align-items-center`}
                                            onClick={() => setActiveTab(item.id)}
                                            type="button"
                                            role="tab"
                                            style={{whiteSpace: 'nowrap'}}
                                        >
                                            {item.status === 'completed' && <span className="me-1">✓</span>}
                                            {item.status === 'failed' && <span className="me-1">✗</span>}
                                            <span className="text-truncate" style={{maxWidth: '150px'}} title={item.fileName}>
                                                {item.fileName}
                                            </span>
                                            {item.processingTime && (
                                                <small className="ms-2 text-muted">({item.processingTime})</small>
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>

                            {/* Tab Content */}
                            <div className="tab-content p-3">
                                {props.uploadQueue.filter(item => ['completed', 'failed'].includes(item.status)).map(item => (
                                    <div
                                        key={item.id}
                                        className={`tab-pane fade ${activeTab === item.id ? 'show active' : ''}`}
                                        role="tabpanel"
                                    >
                                        {activeTab === item.id && (
                                            <>
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <div>
                                                        <h6 className="mb-1">
                                                            {item.fileName}
                                                            {item.status === 'completed' && (
                                                                <span className="badge bg-success ms-2">✓ Completed</span>
                                                            )}
                                                            {item.status === 'failed' && (
                                                                <span className="badge bg-danger ms-2">✗ Failed</span>
                                                            )}
                                                        </h6>
                                                        {item.processingTime && (
                                                            <small className="text-muted">⏱️ Processing time: {item.processingTime}</small>
                                                        )}
                                                    </div>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => {
                                                            props.onRemoveFromQueue(item.id);
                                                            // Switch to another tab if current is removed
                                                            const remainingCompleted = props.uploadQueue.filter(
                                                                i => ['completed', 'failed'].includes(i.status) && i.id !== item.id
                                                            );
                                                            if (remainingCompleted.length > 0) {
                                                                setActiveTab(remainingCompleted[0].id);
                                                            } else {
                                                                setActiveTab(null);
                                                            }
                                                        }}
                                                        title="Remove from list"
                                                    >
                                                        🗑️ Remove
                                                    </button>
                                                </div>

                                                {item.status === 'completed' && item.result && (
                                                    <ResultDisplay result={item.result} isLoading={false} uploadProgress={100} fileName={item.fileName} />
                                                )}

                                                {item.status === 'failed' && item.error && (
                                                    <div className="alert alert-danger mb-0">
                                                        <strong>Error:</strong> {item.error}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuthenticatedContent;